/* ════════════════════════════════════════════════════════════════════
   neoCheckerProtokoll.js
   Frontend für den Änderungsprotokoll-Spoiler im neoChecker-Widget.
   DOM-Zugriffe sind auf das Widget-Wurzelelement (rootEl) beschränkt.
   Nutzt stammdatenService, um IDs in lesbare Bezeichnungen aufzulösen.

   Verwendung in neoChecker.js  →  siehe Protokoll-Integration.md
   ════════════════════════════════════════════════════════════════════ */
import stammdatenService from "./stammdatenService.js";
import basedataService from "./basedataService.js";

/* ── Feld-Labels (lesbar, deutsch) — nach Bedarf erweitern ── */
const FIELD_LABELS = {
  Uebersicht: "Übersicht",
  KurzfristigTage: "Kurzfristig (Tage)",
  Beschaeftigung: "Beschäftigung",
  Beschaeftigungsart: "Beschäftigungsart",
  GutzeitVorwert: "Gutzeit-Vorwert",
  Verguetung: "Vergütung",
  QualifikationID: "Qualifikation",
  Betriebsstaette: "Betriebsstätte",
  NettoBeAbzuege: "Netto-Be-/Abzüge",
  Basisgrundlohn: "Basisgrundlohn",
  Entlohnung: "Entlohnungsform",
  Zielbrutto: "Zielbrutto",
  Eintrittsdatum: "Eintrittsdatum",
  BefristetBis: "Befristet bis",
  ProbezeitBis: "Probezeit bis",
  Steuer: "Steuer",
  Monatsfreibetrag: "Monatsfreibetrag",
  Personalien: "Personalien",
  Geburtsdatum: "Geburtsdatum",
  PrivateKrankenkasse: "Private Krankenkasse",
  PrivatVersichert: "Privat versichert",
  Beitrag: "Beitrag",
  BeitragBasistarif: "Beitrag (Basistarif)",
  FreiwilligeGesetzlicheKrankenversicherung: "Freiw. ges. Krankenversicherung",
  FreiwilligeGesetzlichePflegeversicherung: "Freiw. ges. Pflegeversicherung",
  FreiwilligVersichert: "Freiwillig versichert",
};

/* ── HTML-Markup für das Widget-Template (in den Konstruktor einsetzen) ── */
export const PROTOKOLL_MARKUP = `
  <div class="todo-protokoll" data-protokoll>
    <button class="np-toggle" data-np-toggle aria-expanded="false">
      <i class="bi bi-clock-history np-tico"></i>
      <span class="np-label" data-np-label>Änderungsprotokoll anzeigen</span>
      <span class="np-badge" data-np-badge>0</span>
      <i class="bi bi-chevron-down np-caret"></i>
    </button>
    <div class="np-wrap">
      <div class="np-inner">
        <div class="np-list" data-np-list></div>
      </div>
    </div>
  </div>`;

/* ── Systemgepflegte Felder ausblenden: Uebersicht wird automatisch
      berechnet und ist keine manuelle Änderung — bis auf diese Felder ── */
const UEBERSICHT_SICHTBAR = ["Notizen"];
// Lohnarten-Detailfelder, die beim Umsortieren des Arrays nur Diff-Spam
// erzeugen — ausgeblendet bleibt erkennbar, dass es nur ein Resort war
const LOHNARTEN_AUSGEBLENDET = ["Steuer", "Sozialversicherung", "LohnartTyp", "FiBuKonto", "InExport", "Prozent", "Rechengroesse"];
function istSichtbar(diff) {
  const [root, child] = diff.path;
  const leaf = diff.path[diff.path.length - 1];
  if (root === "Uebersicht" && !UEBERSICHT_SICHTBAR.includes(child)) return false;
  // _id-"Änderungen" in Vergütungen/Lohnarten entstehen nur durch Umsortierung
  // oder Entfernen anderer Einträge, nicht durch manuelles Bearbeiten
  if ((diff.path.includes("Verguetung") || diff.path.includes("Lohnarten")) && leaf === "_id") return false;
  if (diff.path.includes("Lohnarten") && LOHNARTEN_AUSGEBLENDET.includes(leaf)) return false;
  // Sofortmelder-Historie wird vom System geschrieben, keine manuelle Änderung
  if (diff.path.includes("SofortmelderHistorie")) return false;
  return true;
}

/* ── Helfer ── */
function humanize(key) {
  return String(key).replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}
function labelFor(key) { return FIELD_LABELS[key] || humanize(key); }

// ID-Pfadsegmente aus dem key-basierten Diff (arrayToObjectById),
// bei Duplikaten hängt das Backend "_<index>" an
const OBJECT_ID_SEGMENT = /^[0-9a-f]{24}(_\d+)?$/i;

function resolvePathId(arrayKey, id) {
  const unternehmen = stammdatenService.unternehmensobjekt;
  if (!unternehmen) return null;
  const cleanId = id.split("_")[0];
  const findById = (arr) => (arr || []).find(e => String(e._id) === cleanId);
  switch (arrayKey) {
    case "Lohnarten":
    case "NettoBeAbzuege":
      return findById(unternehmen[arrayKey])?.Bezeichnung || null;
    case "Qualifikationen":
      return findById(unternehmen.Qualifikationen)?.BezeichnungNeutral || null;
    case "Pausenmodelle":
      return findById(unternehmen.Pausenmodelle)?.Bezeichnung || null;
    case "Bereiche":
      return findById(unternehmen.Bereiche)?.Bereich || null;
    case "Verzeichnisse":
      return findById(unternehmen.Verzeichnisse)?.VerzeichnisName || null;
    case "Posten":
      return findById((unternehmen.Betriebsstaette || []).flatMap(bs => bs.Posten || []))?.Posten || null;
    case "Einsatzorte":
      return findById((unternehmen.Betriebsstaette || []).flatMap(bs => bs.Einsatzorte || []))?.Bezeichnung || null;
    default:
      return null;
  }
}

function buildCrumbs(path) {
  const segs = [];
  let lastKey = null;
  for (const item of path) {
    if (typeof item === "number") {
      // Index 0 nicht anzeigen ("Beschäftigung" statt "Beschäftigung 1"),
      // bei Vergütung aber immer, damit erkennbar ist, welche geändert wurde
      if (segs.length && (item > 0 || lastKey === "Verguetung")) segs[segs.length - 1].index = item + 1;
    } else if (OBJECT_ID_SEGMENT.test(item)) {
      // ID statt Index: wenn möglich in einen Namen auflösen, sonst ausblenden
      if (segs.length) segs[segs.length - 1].index = resolvePathId(lastKey, item);
    } else {
      lastKey = item;
      segs.push({ label: labelFor(item), index: null });
    }
  }
  return segs;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function isEmpty(v) { return v === null || v === undefined || v === ""; }
function esc(s) {
  return String(s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}
function fmtValue(v) {
  if (isEmpty(v)) return { kind: "empty", text: "leer" };
  if (typeof v === "boolean") return { kind: "bool", on: v, text: v ? "Ja" : "Nein" };
  if (typeof v === "string" && ISO_DATE.test(v)) {
    const [y, m, d] = v.split("-");
    return { kind: "text", text: `${d}.${m}.${y}` };
  }
  if (typeof v === "number") return { kind: "text", text: v.toLocaleString("de-DE") };
  return { kind: "text", text: String(v) };
}

function dateKey(iso) { return new Date(iso).toISOString().slice(0, 10); }
function fmtDay(iso) {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
function relTime(iso) {
  const days = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (days <= 0) return "heute";
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} T.`;
  const w = Math.floor(days / 7);
  if (w < 5) return `vor ${w} Wo.`;
  return `vor ${Math.floor(days / 30)} Mon.`;
}
function initials(name) {
  const p = String(name || "").replace(",", "").trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
}

/* ── IDs in lesbare Bezeichnungen auflösen (Pausenmodell aus dem
      Unternehmen, Einsatzorte/Posten aus den Betriebsstätten) ── */
function resolveValue(path, value) {
  if (isEmpty(value)) return value;
  // Beschäftigungsart ist eine ID (BeschID) aus dem Basedata-Provider
  if (path.includes("Beschaeftigungsart")) {
    const art = (basedataService.beschaeftigungsarten || []).find(b => String(b.BeschID) === String(value));
    return art ? art.BeschaeftigungsartKurz : value;
  }
  const unternehmen = stammdatenService.unternehmensobjekt;
  if (!unternehmen) return value;
  if (path.includes("Pausenmodell") || path.includes("PausenmodellStandard")) {
    const modell = (unternehmen.Pausenmodelle || []).find(p => String(p._id) === String(value));
    return modell ? modell.Bezeichnung : value;
  }
  if (path.includes("Pausenmodelle") && typeof value === "object") {
    // ganzes Pausenmodell (hinzugefügt/entfernt)
    if (value.Bezeichnung) return value.Bezeichnung;
    // einzelner Pausenstandard-Eintrag
    if (value.Nach !== undefined) {
      return `nach ${value.Nach} · ${value.Dauer} Min. · ${value.Bezahlt ? "bezahlt" : "unbezahlt"}`;
    }
  }
  if (path.includes("Einsatzorte")) {
    const id = typeof value === "object" ? value.EinsatzortID : value;
    const ort = (unternehmen.Betriebsstaette || []).flatMap(bs => bs.Einsatzorte || [])
      .find(e => String(e._id) === String(id));
    if (ort) return ort.Bezeichnung;
    if (typeof value !== "object") return value;
    // sonst: generischer Objekt-Fallback unten
  }
  if (path.includes("Posten")) {
    const id = typeof value === "object" ? value.PostenID : value;
    const posten = (unternehmen.Betriebsstaette || []).flatMap(bs => bs.Posten || [])
      .find(p => String(p._id) === String(id));
    if (posten) return posten.Posten;
    if (typeof value !== "object") return value;
  }
  if (path.includes("QualifikationID")) {
    const quali = (unternehmen.Qualifikationen || []).find(q => String(q._id) === String(value));
    return quali ? quali.BezeichnungNeutral : value;
  }
  if (path.includes("Verguetung") && typeof value === "object") {
    // ganze Vergütung (hinzugefügt/entfernt): über die Qualifikation benennen
    const quali = (unternehmen.Qualifikationen || []).find(q => String(q._id) === String(value.QualifikationID));
    if (quali) return quali.BezeichnungNeutral;
  }
  if ((path.includes("Lohnarten") || path.includes("NettoBeAbzuege")) && typeof value === "object") {
    const text = [value.Lohnartennummer, value.Bezeichnung].filter(v => !isEmpty(v)).join(" · ");
    return text || value;
  }
  // generischer Fallback für ganze Objekte: erstes bekanntes
  // Bezeichnungsfeld anzeigen statt "[object Object]"
  if (typeof value === "object") {
    const name = [
      value.Bezeichnung, value.BezeichnungNeutral, value.VerzeichnisName,
      value.Bereich, value.Posten, value.BetriebsstaetteName,
      value.Betriebsnummer, value.EinsatzortID, value.PostenID,
    ].find(v => !isEmpty(v));
    if (name !== undefined) return name;
  }
  return value;
}

function valPill(value, role) {
  const f = fmtValue(value);
  const cls = "np-val " + (f.kind === "empty" ? "empty" : role);
  return `<span class="${cls}">${esc(f.text)}</span>`;
}

function diffRow(diff) {
  const crumbs = buildCrumbs(diff.path);
  const leaf = crumbs[crumbs.length - 1];
  const ancestors = crumbs.slice(0, -1);
  const fullPath = crumbs.map(c => c.label + (c.index ? " " + c.index : "")).join(" › ");
  const crumbHtml = ancestors.length
    ? `<span class="np-crumb">${ancestors.map(c => esc(c.label) + (c.index ? " " + c.index : "")).join('<i class="bi bi-chevron-right"></i>')}<i class="bi bi-chevron-right"></i></span>`
    : "";
  return `
    <div class="np-diff">
      <div class="np-field" title="${esc(fullPath)}">${crumbHtml}${esc(leaf.label)}${leaf.index ? " " + leaf.index : ""}</div>
      <div class="np-change">
        ${valPill(resolveValue(diff.path, diff.oldValue), "old")}
        <span class="np-arrow"><i class="bi bi-arrow-right"></i></span>
        ${valPill(resolveValue(diff.path, diff.value), "new")}
      </div>
    </div>`;
}

function entryHtml(doc) {
  const diffs = (doc.fieldDiffs || []).map(diffRow).join("");
  return `
    <div class="np-entry">
      <div class="np-ehead">
        <span class="np-time">${fmtTime(doc.Datum)} Uhr</span>
        <span class="np-rel">· ${relTime(doc.Datum)}</span>
        <span class="np-user" title="${esc(doc.User || "")}">${initials(doc.User)}</span>
      </div>
      ${diffs}
    </div>`;
}

/* ════════════════════════════════════════════════════════════════════
   setupProtokoll(rootEl)
   Verdrahtet den Auslöser (Aufklappen + localStorage) und liefert ein
   Objekt mit render(docs) zurück.

     const protokoll = setupProtokoll(this.todo);
     protokoll.render(arrayAusCollection);
   ════════════════════════════════════════════════════════════════════ */
export function setupProtokoll(rootEl) {
  const root   = rootEl.querySelector("[data-protokoll]");
  const btn    = rootEl.querySelector("[data-np-toggle]");
  const label  = rootEl.querySelector("[data-np-label]");
  const listEl = rootEl.querySelector("[data-np-list]");
  const badge  = rootEl.querySelector("[data-np-badge]");

  const apply = (open) => {
    root.classList.toggle("open", open);
    btn.setAttribute("aria-expanded", String(open));
    if (label) label.textContent = open ? "Änderungsprotokoll" : "Änderungsprotokoll anzeigen";
  };

  // immer eingeklappt starten
  apply(false);
  btn.addEventListener("click", () => {
    apply(!root.classList.contains("open"));
  });

  function render(docs) {
    const entries = (docs || [])
      .map(d => ({ ...d, fieldDiffs: (d.fieldDiffs || []).filter(istSichtbar) }))
      .filter(d => d.fieldDiffs.length)
      .sort((a, b) => new Date(b.Datum) - new Date(a.Datum));
    const total = entries.reduce((s, e) => s + (e.fieldDiffs ? e.fieldDiffs.length : 0), 0);
    if (badge) badge.textContent = total;

    if (!entries.length) {
      listEl.innerHTML = `
        <div class="np-empty-state">
          <i class="bi bi-clock-history"></i>
          <span>Noch keine Änderungen protokolliert.</span>
        </div>`;
      return;
    }

    const groups = [];
    for (const e of entries) {
      const k = dateKey(e.Datum);
      let g = groups.find(x => x.key === k);
      if (!g) { g = { key: k, iso: e.Datum, items: [] }; groups.push(g); }
      g.items.push(e);
    }
    listEl.innerHTML = groups.map(g => `
      <div class="np-day"><i class="bi bi-calendar3"></i>${fmtDay(g.iso)}</div>
      ${g.items.map(e => entryHtml(e)).join("")}
    `).join("");
  }

  return { render };
}
